<template>
  <div>
    <Head title="Reporte Diario Consolidado de Cajas" />

    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors shadow-sm"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
            Reporte Diario Consolidado de Cajas
          </h1>
          <p class="text-xs md:text-sm text-gray-500 font-medium mt-0.5">
            Monitoreo en una sola vista de apertura, retiros, cierre y diferencias por cajero
          </p>
        </div>
      </div>

      <!-- Date & Store Filters -->
      <div class="flex flex-wrap items-center gap-3 bg-white p-2.5 rounded-xl border border-gray-200 shadow-sm">
        <div v-if="organizations && organizations.length > 1" class="flex items-center gap-2">
          <label class="text-xs font-bold text-gray-600 uppercase">
            <i class="fa fa-store text-slate-400 mr-1"></i>Tienda:
          </label>
          <select
            v-model="filters.organization_id"
            @change="applyFilter"
            class="text-xs font-semibold rounded-lg border-gray-300 py-1.5 px-3 bg-gray-50 focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
          >
            <option value="">Todas las Tiendas</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <label class="text-xs font-bold text-gray-600 uppercase">
            <i class="fa fa-calendar-alt text-teal-600 mr-1"></i>Fecha:
          </label>
          <input
            type="date"
            v-model="filters.date"
            @change="applyFilter"
            class="text-xs font-bold text-gray-800 rounded-lg border-gray-300 py-1.5 px-3 bg-gray-50 focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
          />
        </div>
      </div>
    </div>

    <!-- KPIs Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
        <div class="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 text-xl font-black">
          {{ kpis.total }}
        </div>
        <div>
          <span class="block text-xs font-bold uppercase text-gray-500">Cajas Operadas</span>
          <span class="text-lg font-black text-gray-800">{{ kpis.total }} {{ kpis.total === 1 ? 'caja' : 'cajas' }}</span>
        </div>
      </div>

      <div class="bg-white p-4 rounded-xl border border-emerald-200 shadow-sm flex items-center gap-3">
        <div class="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700 text-xl font-black">
          {{ kpis.cuadradas }}
        </div>
        <div>
          <span class="block text-xs font-bold uppercase text-emerald-700">Cuadradas</span>
          <span class="text-lg font-black text-emerald-800">{{ kpis.cuadradas }} exactas</span>
        </div>
      </div>

      <div class="bg-white p-4 rounded-xl border border-red-200 shadow-sm flex items-center gap-3">
        <div class="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center text-red-600 text-xl font-black">
          {{ kpis.faltantes }}
        </div>
        <div>
          <span class="block text-xs font-bold uppercase text-red-700">Con Faltante</span>
          <span class="text-lg font-black text-red-700">{{ kpis.faltantes }} cajas</span>
        </div>
      </div>

      <div class="bg-white p-4 rounded-xl border border-blue-200 shadow-sm flex items-center gap-3">
        <div class="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 text-xl font-black">
          {{ kpis.sobrantes }}
        </div>
        <div>
          <span class="block text-xs font-bold uppercase text-blue-700">Con Sobrante</span>
          <span class="text-lg font-black text-blue-700">{{ kpis.sobrantes }} cajas</span>
        </div>
      </div>
    </div>

    <!-- Main Consolidated Table (Idéntica al boceto de Daniel) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
      <div class="px-5 py-4 border-b border-gray-200 bg-gray-50/70 flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <i class="fa fa-table text-teal-700"></i>
          <h2 class="font-extrabold text-sm md:text-base text-gray-800 uppercase tracking-wide">
            Consolidado de Cajas del Día ({{ formatDateHeader(selectedDate) }})
          </h2>
        </div>
        <span class="text-xs font-semibold text-gray-500">
          Resultados: <strong class="text-emerald-700">Verde = Cuadrado</strong> | <strong class="text-red-600">Rojo = Faltante</strong> | <strong class="text-blue-600">Azul = Sobrante</strong>
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-xs text-left border-collapse">
          <thead>
            <tr class="bg-slate-100/90 text-slate-800 uppercase font-black text-[11px] border-b border-slate-200">
              <th class="py-3 px-4">Usuario / Caja</th>
              <th class="py-3 px-4 text-center">Apertura</th>
              <th class="py-3 px-4 text-center">Retiros</th>
              <th class="py-3 px-4 text-center">Cierre (Conteo)</th>
              <th class="py-3 px-4 text-center">Diferencia</th>
              <th class="py-3 px-4 text-center">Estado</th>
              <th class="py-3 px-4 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(row, idx) in dailyRows"
              :key="idx"
              class="hover:bg-slate-50/80 transition-colors"
              :class="row.result === 'faltante' ? 'bg-red-50/20' : (row.result === 'sobrante' ? 'bg-blue-50/20' : '')"
            >
              <!-- Usuario y Caja -->
              <td class="py-3.5 px-4 font-medium text-gray-900">
                <div class="flex items-center gap-2">
                  <div class="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-black text-xs uppercase">
                    {{ (row.cashier_name || 'C')[0] }}
                  </div>
                  <div>
                    <div class="font-black text-sm text-gray-900 leading-tight">
                      {{ row.cashier_name }}
                    </div>
                    <div class="text-[11px] text-gray-500 font-semibold flex items-center gap-1.5 mt-0.5">
                      <span>{{ row.register_name }}</span>
                      <span class="text-gray-300">•</span>
                      <span class="text-slate-600 font-bold">{{ row.organization_name }}</span>
                      <span v-if="row.time" class="text-gray-400 text-[10px]">({{ row.time }})</span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Apertura (Bs, USD, COP) -->
              <td class="py-3 px-4 text-center">
                <div class="inline-block text-left text-[11px] space-y-0.5">
                  <div class="font-bold text-gray-900"><span class="text-gray-400 font-normal">USD:</span> ${{ formatCurrency(row.opening.usd) }}</div>
                  <div class="font-semibold text-teal-800"><span class="text-gray-400 font-normal">COP:</span> {{ formatCurrency(row.opening.cop, 0) }}</div>
                  <div class="font-medium text-gray-600"><span class="text-gray-400 font-normal">BS:</span> Bs. {{ formatCurrency(row.opening.bs) }}</div>
                </div>
              </td>

              <!-- Retiros (Bs, USD, COP) -->
              <td class="py-3 px-4 text-center">
                <div class="inline-block text-left text-[11px] space-y-0.5">
                  <div :class="row.withdrawals.usd > 0 ? 'font-bold text-amber-700' : 'text-gray-400'">
                    <span class="text-gray-400 font-normal">USD:</span> ${{ formatCurrency(row.withdrawals.usd) }}
                  </div>
                  <div :class="row.withdrawals.cop > 0 ? 'font-bold text-amber-700' : 'text-gray-400'">
                    <span class="text-gray-400 font-normal">COP:</span> {{ formatCurrency(row.withdrawals.cop, 0) }}
                  </div>
                  <div :class="row.withdrawals.bs > 0 ? 'font-bold text-amber-700' : 'text-gray-400'">
                    <span class="text-gray-400 font-normal">BS:</span> Bs. {{ formatCurrency(row.withdrawals.bs) }}
                  </div>
                </div>
              </td>

              <!-- Cierre / Conteo (Bs, USD, COP) -->
              <td class="py-3 px-4 text-center">
                <div class="inline-block text-left text-[11px] space-y-0.5">
                  <div class="font-black text-gray-900"><span class="text-gray-400 font-normal">USD:</span> ${{ formatCurrency(row.closing.usd) }}</div>
                  <div class="font-bold text-teal-900"><span class="text-gray-400 font-normal">COP:</span> {{ formatCurrency(row.closing.cop, 0) }}</div>
                  <div class="font-semibold text-gray-700"><span class="text-gray-400 font-normal">BS:</span> Bs. {{ formatCurrency(row.closing.bs) }}</div>
                </div>
              </td>

              <!-- Diferencia (Colores: Verde Cuadrado, Rojo Faltante, Azul Sobrante) -->
              <td class="py-3 px-4 text-center">
                <div class="inline-block text-left text-[11px] space-y-0.5 font-black">
                  <!-- USD Diff -->
                  <div :class="diffClass(row.differences.usd, 'usd')">
                    <span class="text-gray-400 font-normal">USD:</span>
                    {{ row.differences.usd > 0 ? '+' : '' }}${{ formatCurrency(row.differences.usd) }}
                  </div>

                  <!-- COP Diff -->
                  <div :class="diffClass(row.differences.cop, 'cop')">
                    <span class="text-gray-400 font-normal">COP:</span>
                    {{ row.differences.cop > 0 ? '+' : '' }}{{ formatCurrency(row.differences.cop, 0) }}
                  </div>

                  <!-- BS Diff -->
                  <div :class="diffClass(row.differences.bs, 'bs')">
                    <span class="text-gray-400 font-normal">BS:</span>
                    {{ row.differences.bs > 0 ? '+' : '' }}Bs. {{ formatCurrency(row.differences.bs) }}
                  </div>
                </div>
              </td>

              <!-- Estado Badge -->
              <td class="py-3 px-4 text-center">
                <span
                  v-if="row.result === 'cuadrado'"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300"
                >
                  <i class="fa fa-check-circle"></i> CUADRADA
                </span>
                <span
                  v-else-if="row.result === 'faltante'"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-red-100 text-red-800 border border-red-300 animate-pulse"
                >
                  <i class="fa fa-exclamation-triangle"></i> FALTANTE
                </span>
                <span
                  v-else-if="row.result === 'sobrante'"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-blue-100 text-blue-800 border border-blue-300"
                >
                  <i class="fa fa-info-circle"></i> SOBRANTE
                </span>

                <div class="mt-1 text-[10px] font-bold text-gray-500">
                  {{ row.status }}
                </div>
              </td>

              <!-- Acciones: Imprimir Vale / Memorándum -->
              <td class="py-3 px-4 text-center">
                <div class="flex items-center justify-center gap-1.5">
                  <a
                    :href="`/cash-registers/daily-slip/${row.closure_id || row.cash_register_id}`"
                    target="_blank"
                    class="px-2.5 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg font-bold text-[11px] shadow-sm flex items-center gap-1.5 transition-colors"
                    title="Imprimir Vale / Memorándum de Caja"
                  >
                    <i class="fa fa-print"></i>
                    <span>Vale / Ticket</span>
                  </a>

                  <Link
                    v-if="row.closure_id"
                    :href="`/cash-closures/${row.closure_id}`"
                    class="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Ver detalle del cierre"
                  >
                    <i class="fa fa-eye text-sm"></i>
                  </Link>
                </div>
              </td>
            </tr>

            <!-- Sin registros -->
            <tr v-if="!dailyRows || dailyRows.length === 0">
              <td colspan="7" class="py-8 text-center text-gray-500 font-semibold text-sm">
                <i class="fa fa-info-circle text-gray-400 text-xl block mb-2"></i>
                No se encontraron cajas abiertas ni cerradas para la fecha seleccionada.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'CashRegisterDailyReport',

  layout: Layout,

  components: {
    Head,
    Link,
  },

  props: {
    selectedDate: String,
    selectedOrg: [Number, String, null],
    organizations: Array,
    dailyRows: Array,
    kpis: Object,
  },

  data() {
    return {
      filters: {
        date: this.selectedDate || new Date().toISOString().split('T')[0],
        organization_id: this.selectedOrg || '',
      },
    }
  },

  methods: {
    back() {
      this.$inertia.visit('/cash-registers')
    },

    applyFilter() {
      this.$inertia.get(
        '/cash-registers/daily-report',
        {
          date: this.filters.date,
          organization_id: this.filters.organization_id || undefined,
        },
        { preserveState: true, replace: true }
      )
    },

    formatCurrency(val, decimals = 2) {
      const num = parseFloat(val) || 0
      return num.toLocaleString('es-VE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    },

    formatDateHeader(dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },

    diffClass(amount, currency) {
      const val = parseFloat(amount) || 0
      const threshold = currency === 'cop' ? 50 : 0.01

      if (Math.abs(val) < threshold) {
        return 'text-emerald-600 font-bold'
      } else if (val < -threshold) {
        return 'text-red-600 font-black'
      } else {
        return 'text-blue-600 font-black'
      }
    },
  },
}
</script>
